import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Header } from '../../components/layout/Header';
import { fetchProjects } from '../../lib/api/projects';
import { useAuth } from '../../lib/auth/AuthContext';
import type { ProjectSummary } from '../../../../shared/types/projects';
import { Button, EmptyState, PageHeader, StatusBadge } from '../../components/ui';

export const ProjectsListPage: React.FC = () => {
  const { currentAccount } = useAuth();
  const query = useQuery({
    queryKey: ['projects'],
    queryFn: () => fetchProjects({ limit: 100 }),
  });

  // Scope the list to the active workspace. The backend already limits
  // the response to accounts the user belongs to; here we narrow further
  // to the one currently selected in the header. Projects with no
  // account_id (legacy, unclaimed) are kept visible so they never vanish.
  // Archived (soft-deleted) projects are hidden — the data is preserved
  // server-side and can be recovered by an operator if needed.
  const visibleProjects = useMemo(() => {
    const all = query.data?.data.projects ?? [];
    const live = all.filter((p) => !p.archived);
    if (!currentAccount) return live;
    return live.filter((p) => p.accountId === currentAccount.id || p.accountId === null);
  }, [query.data, currentAccount]);

  return (
    <div data-testid="projects-page" className="min-h-screen bg-slate-50">
      <Header title="Projects" />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 space-y-5">
        <PageHeader
          title="Projects"
          description="Forge projects in this workspace. A project groups related repos and shares settings (model selection, guardrails). The plan is set once at the workspace level and applies to every project."
          actions={
            <Link to="/projects/new">
              <Button variant="primary" size="md">
                New project
              </Button>
            </Link>
          }
        />

        {query.isLoading && (
          <div className="text-sm text-slate-500">Loading projects…</div>
        )}

        {query.isError && (
          <div role="alert" className="text-sm text-danger-700 bg-danger-50 border border-danger-100 rounded-md px-3 py-2">
            {(query.error as Error).message}
          </div>
        )}

        {query.isSuccess && visibleProjects.length === 0 && (
          <EmptyState
            testid="projects-empty-state"
            icon="📁"
            title={currentAccount ? `No projects in ${currentAccount.name} yet` : 'No projects yet'}
            description="Create your first project to start grouping repos and managing settings."
            action={
              <Link to="/projects/new">
                <Button variant="primary">Create your first project</Button>
              </Link>
            }
          />
        )}

        {query.isSuccess && visibleProjects.length > 0 && (
          <ProjectsTable projects={visibleProjects} />
        )}
      </main>
    </div>
  );
};

const HEADER_CLS =
  'px-4 py-2.5 text-2xs uppercase tracking-wide text-slate-500 font-semibold text-left';

const ProjectsTable: React.FC<{ projects: ProjectSummary[] }> = ({ projects }) => (
  <div className="bg-white border border-slate-200 rounded-xl shadow-card overflow-hidden">
    <table className="w-full text-sm" data-testid="projects-table">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className={HEADER_CLS}>ID</th>
          <th className={HEADER_CLS}>Name</th>
          <th className={HEADER_CLS}>Owner</th>
          <th className={HEADER_CLS}>Repos</th>
          <th className={HEADER_CLS}>Updated</th>
          <th className={HEADER_CLS}>Status</th>
        </tr>
      </thead>
      <tbody>
        {projects.map((p) => (
          <tr key={p.id} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60">
            <td className="px-4 py-3 font-mono text-xs">
              <Link
                to={`/projects/${encodeURIComponent(p.id)}`}
                className="text-primary-500 hover:text-primary-600 hover:underline"
              >
                {p.id}
              </Link>
            </td>
            <td className="px-4 py-3 text-slate-900">{p.name}</td>
            <td className="px-4 py-3 text-slate-700">{p.owner ?? '—'}</td>
            <td className="px-4 py-3 tabular-nums text-slate-700">{p.repoCount}</td>
            <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
              {formatTimestamp(p.updatedAt)}
            </td>
            <td className="px-4 py-3">
              {p.archived ? (
                <StatusBadge tone="neutral">archived</StatusBadge>
              ) : (
                <StatusBadge tone="success" dot>active</StatusBadge>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function formatTimestamp(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default ProjectsListPage;
